/* eslint-disable react/button-has-type */
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  IconButton,
  Input,
  List,
  ListDivider,
  ListItem,
  ListItemButton,
  ListItemContent,
  Typography
} from '@mui/joy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Fragment, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { Link, useNavigate } from 'react-router-dom';

import {
  ArrowLeft,
  ArrowRight,
  DisabledByDefaultOutlined,
  DoneAll,
  EditNote
} from '@mui/icons-material';

import MainLayout from '../../components/Layouts/MainLayout';
import Loading from '../../components/Loading/Loading';
import LocaleSelector from '../../components/LandingPage/LocaleSelector';
import debounce from '../../utils/debounce';
import { useGetTrips } from '../../lib/trip';
import { DEFAULT_LOCALE } from '../../constants/defaults';

import { useLocale } from '../../hooks/useLocale';

import { repathImg, REPOSITORIES } from '../../lib/upload';

import Filters from './Filters';
import TripBulk from './TripBulk';

const SITE = import.meta.env.VITE_SITE;

const ICON_STATUS_MAPPER = {
  Active: <DoneAll color="success" />,
  Draft: <EditNote color="disabled" />,
  Inactive: <DisabledByDefaultOutlined sx={{ color: 'red' }} />
};

export default function Trips() {
  const defaultPage = new URLSearchParams(window.location.search).get('page') || 1;
  const defaultSearch = new URLSearchParams(window.location.search).get('search') || '';
  const defaultGuide = new URLSearchParams(window.location.search).get('guide') || '';
  const defaultStatus = new URLSearchParams(window.location.search).get('status') || '';

  const [locale] = useLocale();

  const [openBulkDrawer, setOpenBulkDrawer] = useState(false);

  const navigate = useNavigate();
  const [page, setPage] = useState(Number(defaultPage));
  const [search, setSearch] = useState(
    defaultSearch || defaultGuide || defaultStatus
      ? `?search=${defaultSearch}&locale=${locale || DEFAULT_LOCALE}${
          defaultGuide ? `&guide=${defaultGuide}` : ''
        }${defaultStatus ? `&status=${defaultStatus}` : ''}`
      : ''
  );

  const { data, isLoading, refetch, isRefetching } = useQuery(['allTrips'], () =>
    useGetTrips({ page: page - 1, search, locale })
  );

  useEffect(() => {
    refetch();
  }, [page, search, locale]);

  if (!locale) return null;

  if (isLoading) {
    return (
      <MainLayout title="Trips">
        <Loading />
      </MainLayout>
    );
  }

  if (!data.data)
    return (
      <MainLayout title="Trips">
        <Typography>No trips found</Typography>
      </MainLayout>
    );

  const handleSetNewPage = (newPage) => {
    window.scrollTo(0, 0);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', newPage);
    window.history.pushState({}, '', `/trips?${searchParams.toString()}`);
    setPage(newPage);
  };

  const searchTrip = debounce((e) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', 1);
    setPage(1);
    const searchTerm = e.target.value;
    if (!searchTerm) {
      searchParams.delete('search');
      setSearch(`?${searchParams.toString()}`);
      window.history.pushState({}, '', `/trips?${searchParams.toString()}`);
      return;
    }
    searchParams.set('search', searchTerm);
    window.history.pushState({}, '', `/trips?${searchParams.toString()}`);
    setSearch(`?${searchParams.toString()}`);
  }, 500);

  const handleFilterByGuide = (guideId) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('guide', guideId);
    window.history.pushState({}, '', `/trips?${searchParams.toString()}`);
    setSearch(`?${searchParams.toString()}`);
  };

  const handleFilterByStatus = (status) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('status', status);
    window.history.pushState({}, '', `/trips?${searchParams.toString()}`);
    setSearch(`?${searchParams.toString()}`);
  };

  return (
    <MainLayout
      title="Trips"
      options={
        <>
          <Button onClick={() => navigate('/trip/new')}>Create new trip</Button>
          <Button onClick={() => setOpenBulkDrawer(true)}>Open Bulk</Button>
        </>
      }>
      <TripBulk open={openBulkDrawer} setOpen={setOpenBulkDrawer} bulkPage={[]} />
      <LocaleSelector />
      <Box
        sx={{
          display: 'flex',
          gap: '5px',
          justifyContent: 'center',
          marginBottom: '16px',
          marginTop: '16px'
        }}>
        <Input
          autoFocus
          placeholder="Hiking in Slovenia"
          defaultValue={defaultSearch}
          onChange={searchTrip}
        />
        <Filters
          setSearch={setSearch}
          handleFilterByGuide={handleFilterByGuide}
          handleFilterByStatus={handleFilterByStatus}
        />
      </Box>
      {isRefetching ? (
        <Loading />
      ) : (
        <>
          <List>
            {data.data.map((trip) => {
              const tripData = trip.i18n[locale];
              return (
                <Fragment key={trip._id}>
                  <ListItem
                    startAction={ICON_STATUS_MAPPER[trip.trip_status]}
                    endAction={
                      tripData.link && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          component="a"
                          href={tripData.link.replace('https://www.explore-share.com', SITE)}
                          target="_blank">
                          <OpenInNewIcon />
                        </IconButton>
                      )
                    }>
                    <ListItemButton
                      component={Link}
                      to={`/trip/${trip._id}/options?locale=${locale}`}
                      state={{ trip }}>
                      <img
                        alt=""
                        src={`${repathImg(trip.cover_url, REPOSITORIES.TRIP_COVER, {
                          _id: trip._id
                        })}?w=180&h=180`}
                        height="60px"
                        width="60px"
                        loading="lazy"
                        style={{ borderRadius: '4px', objectFit: 'cover', marginRight: '5px' }}
                      />
                      <ListItemContent>
                        {tripData.title ? (
                          <Typography level="body-lg">{tripData.title}</Typography>
                        ) : (
                          <Typography>Untitled</Typography>
                        )}
                        <Typography level="body-sm">
                          {trip.guide && `${trip.guide.first_name} ${trip.guide.last_name}`}
                        </Typography>
                        <Typography level="body-sm">
                          Updated at:{' '}
                          {dayjs(trip.updatedAt || trip.dato_last_updated).format(
                            'DD/MM/YYYY, HH:mm'
                          )}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListDivider />
                </Fragment>
              );
            })}
          </List>
          {!defaultSearch && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                justifyContent: 'center',
                background: 'white',
                padding: '10px 0px'
              }}>
              <Button
                size="sm"
                variant="outlined"
                disabled={page <= 1}
                onClick={() => handleSetNewPage(page - 1)}>
                <ArrowLeft />
              </Button>
              <Typography>
                {page} / {data.metadata.totalPages}
              </Typography>
              <Button size="sm" variant="outlined" onClick={() => handleSetNewPage(page + 1)}>
                <ArrowRight />
              </Button>
            </Box>
          )}
        </>
      )}
    </MainLayout>
  );
}
